import { Trans, useLingui } from "@lingui/react";
import { Button } from "@warp-ds/elements/react/button";
import { useEffect, useState } from "react";
import { useAdViewAd } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import { Icon } from "../Icon.tsx";
import { useGallery } from "./state.tsx";
import { hasAdSlide } from "./util.ts";

export const Actions = ({ inCarouselAdLoaded }: { inCarouselAdLoaded: boolean }) => {
  const { i18n } = useLingui();
  const { images } = useAdViewAd();
  const { isMobile } = useAppConfig();
  const { currentImageIndex, showNextImage, showPreviousImage, fullScreen } = useGallery();
  const [totalNoOfImages, setTotalNoOfImages] = useState(images.length);

  const imageText = images[currentImageIndex]?.description || "";
  const imageLabel = i18n.t({
    id: "imageGallery.description",
    message: "Image {current} of {total}",
    values: { current: currentImageIndex + 1, total: images.length },
  });

  useEffect(() => {
    setTotalNoOfImages(images.length + (hasAdSlide() && inCarouselAdLoaded ? 1 : 0));
  }, [images, inCarouselAdLoaded]);

  return (
    <>
      {totalNoOfImages > 1 ? (
        <div className="absolute w-full h-full flex justify-between items-center px-16 pointer-events-none z-20">
          <Button
            variant="pill"
            aria-label={i18n.t({ id: "imageGallery.gallery.previous", message: "Go to previous image" })}
            className="pointer-events-auto min-w-48 min-h-48 part-[base]:!bg-[--w-black/70] part-[base]:!hover:bg-[--w-black/90] part-[base]:!s-text-inverted-static transition-transform duration-100 ease-in-out part-[base]:active:scale-95"
            onClick={showPreviousImage}
          >
            <Icon name="ChevronLeft" size="medium" />
          </Button>
          <Button
            variant="pill"
            aria-label={i18n.t({ id: "imageGallery.gallery.next", message: "Go to next image" })}
            className="pointer-events-auto min-w-48 min-h-48 part-[base]:!bg-[--w-black/70] part-[base]:!hover:bg-[--w-black/90] part-[base]:!s-text-inverted-static transition-transform duration-100 ease-in-out part-[base]:active:scale-95"
            onClick={showNextImage}
          >
            <Icon name="ChevronRight" size="medium" />
          </Button>
        </div>
      ) : null}

      <div className="absolute text-center bottom-16 flex flex-col justify-center items-center w-full z-20">
        {images.length > 0 && !(currentImageIndex === images.length) ? (
          <output
            aria-live="polite"
            aria-label={`${imageText} ${imageLabel}`}
            className="rounded-8 p-8 !bg-[--w-black/70] !s-text-inverted-static truncate mx-16 max-w-full"
          >
            <span aria-hidden>{imageText ? `${imageText} ` : null}</span>
            <span aria-hidden>{`(${currentImageIndex + 1}/${totalNoOfImages})`}</span>
          </output>
        ) : null}
      </div>
      {fullScreen && !isMobile ? (
        <div className="absolute text-center top-16 flex flex-col justify-center items-center w-full z-20 disappear">
          <div className="rounded-8 p-8 !bg-[--w-black/70] !s-text-inverted-static mx-16">
            <Trans
              id="imageGallery.fullscreen"
              message={'Use the arrow keys to switch between images, and "Escape" to close fullscreen'}
            />
          </div>
        </div>
      ) : null}
    </>
  );
};
